import * as qs from 'query-string';

/**
 * Create a tabbed interface.
 *
 * @example Required markup:
 *
 *      <!-- tabbed navigation element -->
 *      <nav class="{{wrapper-class}}">
 *        <ul role="tablist">
 *          <!-- tab element -->
 *          <li role="presentation">
 *            <a
 *              role="tab"
 *              href="#{{panel-id}}"
 *              id="{{panel-id}}-tab"
 *              aria-controls="{{panel-id}}"
 *              data-show-panel="false"
 *              data-function="changeDataViz"
 *              data-parameters="line"
 *            >
 *              Tab 1
 *            </a>
 *          </li>
 *        </ul>
 *      </nav>
 *
 *      <!-- tabpanel element -->
 *      <div
 *        tabindex="0"
 *        role="tabpanel"
 *        id="{{panel-id}}"
 *        aria-labelledby="{{panel-id}}-tab"
 *      >
 *        {{> some-content }}
 *      </div>
 *
 * @example Invocation:
 *
 *      createTabs( '.class-name' );
 *
 * @param {String} selector - The css selector (wrapper-class) for the tabbed navigation element.
 */
export default ( selector ) => {

    const tabList = document.querySelector( `${ selector } [role="tablist"]` );

    if ( !tabList ) return;

    const tabs = [ ...document.querySelectorAll( `${ selector } [role="tab"]` ) ];
    const tabPanels = [ ...document.querySelectorAll( `${ selector } ~ [role="tabpanel"]` ) ];

    const query = qs.parse( location.search );

    let tabIndex = 0;

    if ( 'tab' in query ) {

        tabs.forEach( ( tab, index ) => {

            if ( tab.id === query.tab ) {

                tabIndex = index;

            }

        } );

    }

    // Set initial state
    tabs.forEach( ( tab, index ) => {

        tab.setAttribute( 'aria-selected', index === tabIndex );

        index === tabIndex && tab.classList.add( 'active' );

    } );

    tabPanels.forEach( ( panel, index ) => {

        if ( index !== tabIndex ) panel.setAttribute( 'hidden', true );

        panel.setAttribute( 'tabIndex', -1 );

    } );

    tabList.addEventListener( 'click', ( e ) => {

        const selectedTab = e.target;

        if ( selectedTab.getAttribute( 'role' ) !== 'tab' ) return;

        e.preventDefault();

        const previousTab = tabs.find( ( tab ) => tab.getAttribute( 'aria-selected' ) === 'true' );
        const previousPanel = tabPanels.find( ( panel ) => !panel.hasAttribute( 'hidden' ) );
        const selectedPanel = tabPanels.find( ( panel ) => selectedTab.getAttribute( 'aria-controls' ) === panel.id );

        // only show tab panels that are designated to show with data-show-panel
        if ( selectedTab.dataset.showPanel !== undefined && selectedTab.dataset.showPanel === 'true' ) {

            // Update state
            previousTab.setAttribute( 'aria-selected', false );
            previousTab.classList.remove( 'active' );
            selectedTab.setAttribute( 'aria-selected', true );
            selectedTab.classList.add( 'active' );


            previousPanel.setAttribute( 'hidden', true );
            selectedPanel.removeAttribute( 'hidden' );

        }
        else {

            // still want to show the panel selected, but also pass in a function or variable to change content of the div
            previousTab.setAttribute( 'aria-selected', false );
            previousTab.classList.remove( 'active' );
            selectedTab.setAttribute( 'aria-selected', true );
            selectedTab.classList.add( 'active' );


            // need to hide tab if comming from data-show-panel='true' to data-show-panel='false'
            if ( previousTab.dataset.showPanel === 'true' ) {

                // show the targeted panel
                selectedPanel.removeAttribute( 'hidden' );
                previousPanel.setAttribute( 'hidden', true );

            }

            if ( selectedTab.dataset.function !== undefined && selectedTab.dataset.function ) {

                const fn = window[selectedTab.dataset.function];

                if ( selectedTab.dataset.parameters !== undefined && selectedTab.dataset.parameters ) {

                    const parameters = selectedTab.dataset.parameters.split( '|' );

                    fn.apply( window, parameters );

                }
                else {

                    fn.apply( window, [] );

                }

            }

        }

    } );

};